<template>
  <div v-show="!isCollapsed" class="appointment-filter-bar">
    <div class="row">
      <div class="col-6">
        <div class="filter-item">
          <div class="label">ผู้ให้บริการ</div>
          <provider-selector
            :value="value.provider"
            @input="handleChange('provider', $event)"
          ></provider-selector>
        </div>
        <div class="filter-item">
          <div class="label">บริการ</div>
          <service-select
            :value="value.service"
            :provider="value.provider"
            @input="handleChange('service', $event)"
          ></service-select>
        </div>
        <div class="filter-item">
          <div class="label">ลูกค้า</div>
          <user-select
            :value="value.user"
            @input="handleChange('user', $event)"
          ></user-select>
        </div>
        <div class="filter-item">
          <div class="label">โครงการ</div>
          <project-select
            :value="value.project"
            @input="handleChange('project', $event)"
          ></project-select>
        </div>
        <div class="filter-item">
          <div class="label">บ้านเลขที่</div>
          <el-select
            :value="value.home"
            :filter-method="handleFilterHomes"
            class="select"
            filterable
            clearable
            placeholder="เลือกบ้าน"
            value-key="homeId"
            label-key="addressNo"
            @input="handleChange('home', $event)"
          >
            <el-option
              v-for="home in projectHomeOptions"
              :key="home.homeId"
              :value="home"
              :label="home.addressNo"
            ></el-option>
          </el-select>
        </div>
      </div>
      <div class="col-6">
        <div v-if="isProductWaitingFulfillPage">
          <div class="filter-item">
            <div class="label">วันซื้อเมื่อ</div>
            <el-date-picker
              :value="purchasedAtRange"
              class="date-picker"
              type="daterange"
              range-separator="ถึง"
              @input="handleChangeDate('purchasedAtRange', $event)"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">สถานะ</div>
            <div class="status created">สินค้าซื้อใหม่</div>
          </div>
        </div>
        <div v-else>
          <div class="filter-item">
            <div class="label">วันนัดหมาย</div>
            <el-date-picker
              :value="dateTimeRange"
              class="date-picker"
              type="daterange"
              range-separator="ถึง"
              @input="handleChangeDate('dateTimeRange', $event)"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">นัดเมื่อ</div>
            <el-date-picker
              :value="createdAtRange"
              class="date-picker"
              type="daterange"
              range-separator="ถึง"
              @input="handleChangeDate('createdAtRange', $event)"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">สถานะ</div>
            <el-select
              :value="value.status"
              @input="handleChange('status', $event)"
            >
              <el-option
                v-for="status in statusOptions"
                :key="status.id"
                :label="status.label"
                :value="status.id"
              ></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <div class="label">Package Group</div>
            <group-select
              :value="value.group"
              @input="handleChange('group', $event)"
            ></group-select>
          </div>
        </div>
      </div>
    </div>
    <div class="action-bar">
      <sc-button variant="secondary" :disabled="isDownloading" @click="handleClickDownloadReportButton"
        >Download Report</sc-button
      >
      <sc-button variant="primary" @click="handleClickSearchButton"
        >ค้นหา</sc-button
      >
    </div>
  </div>
</template>

<script>
import { DatePicker, Option, Select } from "element-ui";
import { DateTime } from "luxon";
import ScButton from "@/lib/components/ScButton";
import ProviderSelector from "@/lib/components/ProviderSelector";
import ProjectSelect from "@/lib/components/ProjectSelect";
import ServiceSelect from "@/lib/components/ServiceSelect";
import UserSelect from "@/lib/components/UserSelect";
import { baanRueJaiClient } from "@/dependencies";
import GroupSelect from "./GroupSelect";

export default {
  components: {
    "el-select": Select,
    "el-option": Option,
    "el-date-picker": DatePicker,
    ProviderSelector,
    ProjectSelect,
    ServiceSelect,
    UserSelect,
    GroupSelect,
    ScButton
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    isProductWaitingFulfillPage: {
      type: Boolean,
      default: false
    },
    isDownloading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isCollapsed: false,
      homeQuery: "",
      projectHomes: [],
    };
  },
  computed: {
    projectHomeOptions() {
      if (!this.homeQuery) {
        return this.projectHomes;
      }

      return this.projectHomes.filter((home) => {
        const [, no] = home.addressNo.split("/");

        if (!no) {
          return home.addressNo.includes(this.homeQuery);
        }

        return no.includes(this.homeQuery);
      });
    },

    dateTimeRange() {
      if (!this.value.dateTimeRange) {
        return null;
      }

      const { from, to } = this.value.dateTimeRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
    createdAtRange() {
      if (!this.value.createdAtRange) {
        return null;
      }

      const { from, to } = this.value.createdAtRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
    statusOptions() {
      return [
        {
          id: "ALL",
          label: "ทั้งหมด"
        },
        {
          id: "CREATED",
          label: "รอผู้ให้บริการยืนยัน"
        },
        {
          id: "CONFIRMED",
          label: "รอรับบริการ"
        },
        {
          id: "CANCELLED",
          label: "ยกเลิก"
        },
        {
          id: "FULFILLED",
          label: "เสร็จสิ้น"
        }
      ];
    },
    purchasedAtRange() {
      if (!this.value.purchasedAtRange) {
        return null;
      }

      const { from, to } = this.value.purchasedAtRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
  },
  watch: {
    "value.project": function watchValueProject(newVal, oldVal) {
      if (newVal === null) {
        return;
      }

      if (oldVal !== null && newVal.projectId === oldVal.projectId) {
        return;
      }

      this.updateProjectHomes();
    }
  },
  created() {
    this.setupForm()
  },
  methods: {
    async setupForm() {
      if (
        this.value
        && this.value.project
        && this.value.project.projectId
      ) {
        await this.updateProjectHomes(this.value.project.projectId)
      }
    },
    async updateProjectHomes(projectId) {
      const result = await this.fetchProjectHomes(projectId);

      this.homeQuery = "";
      this.projectHomes = result;
      this.projectHomeByIds = result.reduce((acc, home) => ({
        ...acc,
        [home.homeId]: home
      }), {});
      this.$emit("input", { ...this.value, homes: [], role: null });
    },
    async fetchProjectHomes() {
      const { projectId } = this.value.project;

      const response = await baanRueJaiClient.admin.memberManagement.projects.homes(
        projectId
      );

      return response.data;
    },
    async handleFilterHomes(query) {
      this.homeQuery = query;
    },
    handleChangeDate(key, value) {
      if (!value) {
        this.$emit("input", { ...this.value, [key]: null });
        return;
      }
      const [fromJSDate, toJSDate] = value;
      const from = DateTime.fromJSDate(fromJSDate).toUTC();
      const to = DateTime.fromJSDate(toJSDate).endOf("day").toUTC();

      this.$emit("input", {
        ...this.value,
        [key]: {
          from,
          to
        }
      });
    },
    handleChange(key, val) {
      this.$emit("input", { ...this.value, [key]: val });
    },
    handleClickSearchButton() {
      this.$emit("search");
    },
    handleClickDownloadReportButton() {
      this.$emit("download");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.appointment-filter-bar {
  border-bottom: $border;
  padding: ($space / 3) $space;
}

.filter-item {
  margin-bottom: 15px;
}

.label {
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.date-picker,
.select {
  width: 100%;
}

.action-bar {
  display: flex;
  justify-content: space-between;
}

.status {
  line-height: 1.2;
  font-weight: bold;

  &.created {
    color: $secondary-color;
  }
}
</style>
